import { createFileRoute, Outlet } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { useCurrentUser } from '~/hooks/useCurrentUser';

/**
 * The Follow-up Network shell: the directory list and its map.
 *
 * A top-level route rather than a tab under `/follow-ups`, since legacy
 * registers it as a sibling tab. Legacy renders the list for any unknown action
 * such as `/bogus`; here those 404.
 *
 * Unlike `follow-ups.tsx`, this shell has a real gate: both children are the
 * directory, so the client gate can mirror the server's requirement exactly.
 * Defence in depth, never the only gate.
 */
function FollowUpNetworkLayout(): ReactElement | null {
  const { me, isPending } = useCurrentUser();

  // `me.profile_type`, not `useCurrentUser().isStudyHelper`, which would admit
  // the study helpers the endpoint refuses.
  const allowed =
    me?.profile_type === 'FOLLOWUP_COORDINATOR' ||
    me?.profile_type === 'FOLLOWUP_PARTNER' ||
    // Admins pass the server's requirement, so a gate without them would render
    // NotFoundScreen over a working response. There's no admin nav item for
    // this.
    me?.profile_type === 'ADMIN';

  // `/users/me` is pending on first paint and a bare `!allowed` bounces every
  // caller, the coordinator included.
  if (isPending) return null;
  if (!allowed) return <NotFoundScreen />;

  // No title or toggle here: the two views have different headings, each with a
  // button to the other.
  return <Outlet />;
}

export const Route = createFileRoute('/_app/follow-up-network')({
  component: FollowUpNetworkLayout,
});
